import { AxiosError } from "axios";

import api from "services/api";
import { returnResponse } from "services/utils";

export interface AdGroupMappingDto {
  id: number;
  idMapping: string;
  customerId: number;
  adGroupName: string;
  kentaRoleId: number;
  kentaRoleName?: string;
  defaultPlaceId?: number;
}

export interface AdGroupMappingCreateInput {
  CustomerId: number;
  IdMapping: string;
  AdGroupName: string;
  KentaRoleId: number;
  DefaultPlaceId?: number;
}

export interface AdGroupMappingUpdateInput extends AdGroupMappingCreateInput {
  Id: number;
}

export interface AzureGroupDto {
  groupId: string;
  displayName: string;
}

const BASE_URL = "/v1/AdGroupMapping";

const normalizeArrayPayload = (payload: any): AdGroupMappingDto[] => {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
};

const normalizeSinglePayload = (payload: any): AdGroupMappingDto | null => {
  if (payload?.data && !Array.isArray(payload.data)) {
    return payload.data as AdGroupMappingDto;
  }

  if (payload && !Array.isArray(payload)) {
    return payload as AdGroupMappingDto;
  }

  return null;
};

const buildErrorResponse = (error: unknown) => {
  if (error instanceof AxiosError) {
    const messages = error.response?.data?.errors;
    const { severity, status, message, data } = returnResponse({
      statusCode: error.response?.status,
      data: error.response?.data,
      message: Array.isArray(messages)
        ? messages.at(0)?.message
        : "An unexpected error occurred",
    });

    return {
      severity,
      status,
      message: message || "Error",
      data,
    };
  }

  return {
    status: false,
    message: "Error",
    data: null,
    severity: "error" as const,
  };
};

export const AdGroupMappingService = {
  getAllByCustomerId: async (customerId: number) => {
    try {
      const { data } = await api.get<any>(
        `${BASE_URL}/GetAllByCustomerID?customerId=${customerId}`,
      );

      return {
        status: true,
        data: normalizeArrayPayload(data),
        message: "",
      };
    } catch (error) {
      return buildErrorResponse(error);
    }
  },

  getAzureGroups: async (customerId: number) => {
    try {
      const { data } = await api.get<any>(
        `${BASE_URL}/GetAzureGroups?customerId=${customerId}`,
      );

      return {
        status: true,
        data: normalizeArrayPayload(data) as unknown as AzureGroupDto[],
        message: "",
      };
    } catch (error) {
      return buildErrorResponse(error);
    }
  },

  add: async (mapping: AdGroupMappingCreateInput) => {
    try {
      const body = {
        idMapping: mapping.IdMapping,
        customerId: mapping.CustomerId,
        adGroupName: mapping.AdGroupName,
        kentaRoleId: mapping.KentaRoleId,
        defaultPlaceId: mapping.DefaultPlaceId ?? 0,
      };
      const { data } = await api.post<any>(`${BASE_URL}/Add`, body);

      return {
        status: true,
        data: normalizeSinglePayload(data),
        message: data?.message ?? "",
      };
    } catch (error) {
      return buildErrorResponse(error);
    }
  },

  update: async (mapping: AdGroupMappingUpdateInput) => {
    try {
      const body = {
        id: mapping.Id,
        idMapping: mapping.IdMapping,
        customerId: mapping.CustomerId,
        adGroupName: mapping.AdGroupName,
        kentaRoleId: mapping.KentaRoleId,
        defaultPlaceId: mapping.DefaultPlaceId ?? 0,
      };
      const { data } = await api.put<any>(`${BASE_URL}/Update`, body);

      return {
        status: true,
        data: normalizeSinglePayload(data),
        message: data?.message ?? "",
      };
    } catch (error) {
      return buildErrorResponse(error);
    }
  },

  remove: async (id: number) => {
    try {
      const { data } = await api.delete<any>(`${BASE_URL}/Delete?id=${id}`);

      return {
        status: true,
        data: data?.data ?? data ?? null,
        message: data?.message ?? "",
      };
    } catch (error) {
      return buildErrorResponse(error);
    }
  },

  getByGroupName: async (customerId: number, adGroupName: string) => {
    try {
      const { data } = await api.get<any>(
        `${BASE_URL}/GetByGroupName?customerId=${customerId}&adGroupName=${encodeURIComponent(
          adGroupName,
        )}`,
      );

      return {
        status: true,
        data: normalizeSinglePayload(data),
        message: data?.message ?? "",
      };
    } catch (error) {
      return buildErrorResponse(error);
    }
  },
};
